import type { HoleType, PartType, PointType, SlotType } from '@/partTypes'
import { rolloverPlank } from '@/util/LayoutFuncs'
import { rotate180Part, rotatePart } from '@/util/LayoutTool'
import { customSort, getSlotBasicInfo } from '@/util/plankCommonFuncs'
import {
  Colors,
  DxfWriter,
  LWPolylineFlags,
  TextHorizontalAlignment,
  Units,
  point2d,
  point3d,
} from '@tarikjabiri/dxf'
import FileSaver from 'file-saver'
import JSZip from 'jszip'
import { cloneDeep } from 'lodash'

const SCALE = 3
const TEXTGAP = 4 * SCALE
const STYLE_CHINESE = 'Chinese'
const DXF_TEXT_SCALE = 0.8

function estimateTextWidth(text: string, textHeight: number): number {
  let width = 0
  for (const ch of text) {
    const code = ch.charCodeAt(0)
    if (code > 127) {
      width += textHeight
    } else {
      width += textHeight * 0.5
    }
  }
  return width
}

const LAYER_OUTLINE = 'Outline'
const LAYER_HOLES = 'Holes'
const LAYER_SLOTS = 'Slots'
const LAYER_TEXT = 'Text'
const LAYER_ANNOTATIONS = 'Annotations'
const LAYER_DIMENSIONS = 'Dimensions'
const LAYER_SIDE_HOLES = 'SideHoles'
const LAYER_TABLE = 'Table'
const LAYER_DASHED = 'Dashed'

function addChineseStyle(dxf: DxfWriter) {
  const style = dxf.tables.styleTable.addStyle(STYLE_CHINESE)
  style.fontFileName = 'simsun.ttc'
  style.bigFontFileName = 'gbcbig.shx'
}

function addStandardLayers(dxf: DxfWriter) {
  dxf.addLayer(LAYER_OUTLINE, Colors.White, 'Continuous')
  dxf.addLayer(LAYER_HOLES, Colors.Blue, 'Continuous')
  dxf.addLayer(LAYER_SLOTS, Colors.Green, 'Continuous')
  dxf.addLayer(LAYER_TEXT, Colors.White, 'Continuous')
  dxf.addLayer(LAYER_ANNOTATIONS, Colors.Red, 'Continuous')
  dxf.addLayer(LAYER_DIMENSIONS, Colors.Red, 'Continuous')
  dxf.addLayer(LAYER_SIDE_HOLES, Colors.Cyan, 'Continuous')
  dxf.addLayer(LAYER_TABLE, Colors.White, 'Continuous')
  dxf.addLayer(LAYER_DASHED, Colors.Cyan, 'DASHED')
}

function addDashedLineType(dxf: DxfWriter) {
  dxf.addLType(
    'DASHED',
    '__ __ . __ __ .',
    [12.7, -6.35, 12.7, -6.35, 0, -6.35]
  )
}

function addTextWithStyle(
  dxf: DxfWriter,
  position: ReturnType<typeof point3d>,
  height: number,
  text: string,
  options: any = {}
) {
  const t = dxf.addText(position, height * DXF_TEXT_SCALE, text, options)
  t.textStyle = STYLE_CHINESE
  return t
}

class GenerateHoleSlotDxf {
  private dxf!: DxfWriter
  private pageWidth = 0
  private pageHeight = 0

  private fy(y: number): number {
    return this.pageHeight - y
  }

  constructor(_orientation: 'landscape' | 'portrait' = 'landscape') {
    this.dxf = new DxfWriter()
    this.dxf.setUnits(Units.Millimeters)
    addDashedLineType(this.dxf)
    addChineseStyle(this.dxf)
    addStandardLayers(this.dxf)
  }

  generate(part: PartType, typeTow?: string) {
    if (!part) return
    part = JSON.parse(JSON.stringify(part))
    const { oRect, partName } = part

    const ratio = 1
    const drawWidth = oRect.width
    const drawHeight = oRect.height
    const gap = 30
    const margin = 30
    const titleSpace = 80
    const annoColWidth = typeTow ? 150 : 0

    this.pageWidth = drawWidth * 2 + gap + 2 * (margin + annoColWidth)
    this.pageHeight = drawHeight + titleSpace + 2 * margin

    const frontXBase = margin + annoColWidth
    const frontYBase = titleSpace + margin
    const oppositeXBase = frontXBase + drawWidth + gap
    const oppositeYBase = frontYBase

    const titleHeight = 5 * SCALE
    addTextWithStyle(
      this.dxf,
      point3d(this.pageWidth / 2, this.fy(8 * SCALE)),
      titleHeight,
      `${partName}(${oRect.width}×${oRect.height})`,
      {
        layerName: LAYER_TEXT,
        horizontalAlignment: TextHorizontalAlignment.Center,
        secondAlignmentPoint: point3d(this.pageWidth / 2, this.fy(8 * SCALE)),
      }
    )
    addTextWithStyle(
      this.dxf,
      point3d(this.pageWidth / 2, this.fy(16 * SCALE)),
      titleHeight,
      `板件条码：${part.plankNum}`,
      {
        layerName: LAYER_TEXT,
        horizontalAlignment: TextHorizontalAlignment.Center,
        secondAlignmentPoint: point3d(this.pageWidth / 2, this.fy(16 * SCALE)),
      }
    )

    const labelHeight = 4 * SCALE
    const textCenterWidth = drawWidth / 2
    addTextWithStyle(
      this.dxf,
      point3d(frontXBase + textCenterWidth, this.fy(frontYBase - gap)),
      labelHeight,
      '正面',
      {
        layerName: LAYER_TEXT,
        horizontalAlignment: TextHorizontalAlignment.Center,
        secondAlignmentPoint: point3d(
          frontXBase + textCenterWidth,
          this.fy(frontYBase - gap)
        ),
      }
    )
    addTextWithStyle(
      this.dxf,
      point3d(oppositeXBase + textCenterWidth, this.fy(oppositeYBase - gap)),
      labelHeight,
      '反面',
      {
        layerName: LAYER_TEXT,
        horizontalAlignment: TextHorizontalAlignment.Center,
        secondAlignmentPoint: point3d(
          oppositeXBase + textCenterWidth,
          this.fy(oppositeYBase - gap)
        ),
      }
    )

    this.drawHoleSlot(part, frontXBase, frontYBase, ratio, 'front', typeTow)
    this.drawHoleSlot(
      part,
      oppositeXBase,
      oppositeYBase,
      ratio,
      'opposite',
      typeTow
    )
  }

  private drawHoleSlot(
    part: PartType,
    startX: number,
    startY: number,
    ratio: number,
    type: 'front' | 'opposite',
    typeTow?: string
  ) {
    if (type === 'opposite') {
      part = JSON.parse(JSON.stringify(part))
      rolloverPlank(part)
    }
    const {
      holes,
      slots,
      oRect,
      oPath,
      curveHoles = [],
      millInfo = [],
      thick,
    } = part
    let drawHole: any[] = []
    let drawSlot: any[] = []
    let curveHoleArr: any[] = []
    let millInfoSlot: any[] = []

    drawHole = holes.filter((it) => it.deep >= thick || it.side > 0)
    drawSlot = slots.filter((it) => it.deep >= thick || it.side > 0)
    curveHoleArr = curveHoles.filter((it) => it.deep >= thick || it.side > 0)
    millInfoSlot = millInfo.filter(
      (it: any) => it.depth >= thick || it.side > 0
    )

    let mainShape: any
    const shapes: any[] = []
    if (oPath) {
      let cHoles: PointType[][] = []
      let mSlots: PointType[][] = []
      if (curveHoleArr.length) {
        cHoles = curveHoleArr.map((it: any) => it.path)
      }
      if (millInfoSlot.length) {
        mSlots = millInfoSlot.map((it: any) => it.shape)
      }
      const resPath = [...oPath, ...cHoles, ...mSlots]
      resPath.forEach((it, idx) => {
        const pointArr = it.map((p: any) => ({
          x: p.x * ratio,
          y: p.y * ratio,
        }))
        pointArr[pointArr.length] = { ...pointArr[0] }
        if (idx === 0) {
          mainShape = pointArr
        } else {
          shapes.push(pointArr)
        }
      })
    } else {
      const width = oRect.width * ratio
      const height = oRect.height * ratio
      mainShape = { x: 0, y: 0, width, height }
    }

    drawHole = drawHole.map((it: any, idx: number) => {
      const { diameter, ocenter: center } = it
      const x = center.x * ratio
      const y = center.y * ratio
      const radius = (diameter * ratio) / 2
      return [{ x, y, radius, type: 'hole', holeData: it, idx }]
    })
    drawHole.sort(customSort)

    drawSlot = drawSlot.map((it: any, idx: number) => {
      const { opt1: pt1, opt2: pt2 } = it
      const slot = {} as any
      let dir
      if (pt1.x == pt2.x) {
        slot.width = it.width
        slot.x = Math.min(pt1.x, pt2.x) - it.width / 2
        slot.long = Math.abs(pt1.y - pt2.y)
        slot.y = pt1.y > pt2.y ? pt2.y : pt1.y
        dir = 'h'
      }
      if (pt1.y == pt2.y) {
        slot.width = Math.abs(pt1.x - pt2.x)
        slot.y = Math.min(pt1.y, pt2.y) - it.width / 2
        slot.long = it.width
        slot.x = pt1.x > pt2.x ? pt2.x : pt1.x
        dir = 'v'
      }
      const x = slot.x * ratio
      const y = slot.y * ratio
      const width = slot.width * ratio
      const height = slot.long * ratio
      return { x, y, width, height, type: 'slot', slotData: it, idx, dir }
    })

    if (mainShape.width !== undefined && mainShape.height !== undefined) {
      this.dxf.addRectangle(
        point2d(startX, this.fy(startY)),
        point2d(startX + mainShape.width, this.fy(startY + mainShape.height)),
        { layerName: LAYER_OUTLINE }
      )
    } else if (Array.isArray(mainShape)) {
      const verts = mainShape.map((p: any) => ({
        point: point2d(p.x + startX, this.fy(p.y + startY)),
      }))
      this.dxf.addLWPolyline(verts, {
        flags: LWPolylineFlags.Closed,
        layerName: LAYER_OUTLINE,
      })
    }

    for (const shape of shapes) {
      const verts = shape.map((p: any) => ({
        point: point2d(p.x + startX, this.fy(p.y + startY)),
      }))
      this.dxf.addLWPolyline(verts, {
        flags: LWPolylineFlags.Closed,
        layerName: LAYER_OUTLINE,
      })
    }

    const collectText: any[] = []

    for (const shape of drawHole) {
      const { holeData, x, y, radius } = shape[0]
      this.dxf.addCircle(point3d(x + startX, this.fy(y + startY)), radius, {
        layerName: LAYER_HOLES,
      })
      const { ocenter, diameter } = holeData
      const d = y + startY + radius + TEXTGAP
      const t = `${diameter}孔(${+ocenter.x.toFixed(1) * 1},${
        +ocenter.y.toFixed(1) * 1
      })`
      collectText.push({ text: t, x: x + startX, y: d })
    }

    for (const shape of drawSlot) {
      const { slotData, x, y, width, height, dir: showDir } = shape
      this.dxf.addRectangle(
        point2d(x + startX, this.fy(y + startY)),
        point2d(x + startX + width, this.fy(y + startY + height)),
        { layerName: LAYER_SLOTS }
      )
      const { opt1, opt2, deep, width: slotWidth } = slotData
      const [minx, miny, maxx, maxy] = [
        x + startX,
        y + startY,
        x + startX + width,
        y + startY + height,
      ]
      const w = maxx - minx
      const h = maxy - miny
      const sdir = w < h ? 'h' : 'v'
      let top = { x: minx + w / 2, y: miny - TEXTGAP }
      let center = { x: minx + w / 2, y: miny + h / 2 }
      let bottom = { x: minx + w / 2, y: maxy + TEXTGAP }
      if (sdir === 'v') {
        top = { x: minx - TEXTGAP, y: miny + h / 2 }
        center = { x: minx + w / 2, y: miny + h / 2 }
        bottom = { x: maxx + TEXTGAP, y: miny + h / 2 }
      }
      let [pt1s, pt2s] = [opt1, opt2].sort((a: any, b: any) =>
        sdir === 'v' ? a.x - b.x : a.y - b.y
      )
      pt1s = { x: +pt1s.x.toFixed(1) * 1, y: +pt1s.y.toFixed(1) * 1 }
      pt2s = { x: +pt2s.x.toFixed(1) * 1, y: +pt2s.y.toFixed(1) * 1 }
      const topV = `(${pt1s.x},${pt1s.y})`
      const bottomV = `(${pt2s.x},${pt2s.y})`
      const slotSize = pt1s.x == pt2s.x ? pt1s.y - pt2s.y : pt1s.x - pt2s.x
      const [sw, sh] = [slotSize, slotWidth]
        .sort((a, b) => a - b)
        .map((it) => +Math.abs(Number(it)).toFixed(1) * 1)
      const centerV = `槽(${+deep.toFixed(1) * 1}x${sw}x${sh})`
      collectText.push({ text: topV, x: top.x, y: top.y })
      collectText.push({ text: centerV, x: center.x, y: center.y })
      collectText.push({ text: bottomV, x: bottom.x, y: bottom.y })
    }

    if (typeTow) {
      const annoMargin = 30
      const annoColW = 150
      const colOffset = 120
      const xBaseFront = annoMargin
      const xBaseOpposite = this.pageWidth - annoMargin - annoColW
      const maxSpacing = this.pageHeight - 60
      let spacing = 10 * SCALE
      let amountFront = 0
      let amountOpposite = 0
      collectText.forEach((it, index) => {
        addTextWithStyle(
          this.dxf,
          point3d(it.x, this.fy(it.y - 1.5 * SCALE)),
          3 * SCALE,
          String(index + 1),
          { layerName: LAYER_ANNOTATIONS }
        )
        if (type === 'front') {
          if (spacing > maxSpacing) {
            amountFront += 1
            spacing = 10 * SCALE
          }
          addTextWithStyle(
            this.dxf,
            point3d(
              amountFront === 0
                ? xBaseFront
                : xBaseFront + colOffset * amountFront,
              this.fy(spacing)
            ),
            3 * SCALE,
            String(index + 1) + '：' + it.text,
            {
              layerName: LAYER_ANNOTATIONS,
              horizontalAlignment: TextHorizontalAlignment.Left,
            }
          )
          spacing += 10 * SCALE
        } else {
          if (spacing > maxSpacing) {
            amountOpposite += 1
            spacing = 10 * SCALE
          }
          addTextWithStyle(
            this.dxf,
            point3d(
              amountOpposite === 0
                ? xBaseOpposite
                : xBaseOpposite - colOffset * amountOpposite,
              this.fy(spacing)
            ),
            3 * SCALE,
            String(index + 1) + '：' + it.text,
            {
              layerName: LAYER_ANNOTATIONS,
              horizontalAlignment: TextHorizontalAlignment.Left,
            }
          )
          spacing += 10 * SCALE
        }
      })
    } else {
      collectText.forEach((it) => {
        addTextWithStyle(
          this.dxf,
          point3d(it.x, this.fy(it.y)),
          3 * SCALE,
          it.text,
          { layerName: LAYER_ANNOTATIONS }
        )
      })
    }
  }

  stringify(): string {
    return this.dxf.stringify()
  }

  save(fileName: string) {
    const content = this.stringify()
    const blob = new Blob([content], { type: 'application/dxf' })
    FileSaver.saveAs(blob, `${fileName}.dxf`)
  }
}

type SideHoleObjType = {
  left: PartType['sholes']
  right: PartType['sholes']
  bottom: PartType['sholes']
  top: PartType['sholes']
}

type TableInfoType = {
  date: string
  version: string
  modifiedCount: string
  designer: string
  size: string
  pic: string
  matCode: string
  check: string
  color: string
  count: string
  percent: string
  approval: string
  index: number
  total: number
  plankNum: string
  orderNo: string
  partName: string
  proCount: string
}

type SlotInfoType = ReturnType<typeof getSlotBasicInfo>
type RectType = { x1: number; x2: number; y1: number; y2: number }

class DrawPartViewDxf {
  pageWidth: number
  pageHeight: number
  part?: PartType
  holeOrder = 1
  partWidth = 0
  partHeight = 0
  partScale = 0
  offsetX = 0
  offsetY = 0
  dxf: DxfWriter
  leftPointX = 0
  bottomPointY = 0
  textRectArr: RectType[] = []

  constructor() {
    this.dxf = new DxfWriter()
    this.dxf.setUnits(Units.Millimeters)
    addDashedLineType(this.dxf)
    addChineseStyle(this.dxf)
    addStandardLayers(this.dxf)
    this.pageWidth = 0
    this.pageHeight = 0
  }

  private fy(y: number): number {
    return this.pageHeight - y
  }

  init(part: PartType) {
    this.part = part
    this.holeOrder = 1
    this.textRectArr = []
    const { width: partW, height: partH } = part.oRect
    this.partScale = 1
    this.partWidth = partW
    this.partHeight = partH
    const tableW = 146 * SCALE
    this.offsetX = 200
    this.offsetY = 200
    this.pageWidth = this.offsetX + this.partWidth + 150 + tableW + 30
    this.pageHeight = this.offsetY + this.partHeight + 200
    this.leftPointX = this.offsetX
    this.bottomPointY = this.offsetY + this.partHeight
  }

  private drawSizeInfo(
    startPoint: { x: number; y: number },
    endPoint: { x: number; y: number },
    dir: 'left' | 'right' | 'top' | 'bottom',
    info: string
  ) {
    const { x: startX, y: startY } = startPoint
    const { x: endX, y: endY } = endPoint
    const markWidth = 3 * SCALE
    const textH = 2 * SCALE
    const tw = estimateTextWidth(info, textH)
    let sp1 = { x: 0, y: 0 }
    let sp2 = { x: 0, y: 0 }
    let ep1 = { x: 0, y: 0 }
    let ep2 = { x: 0, y: 0 }

    switch (dir) {
      case 'left':
        sp1 = { x: startX - 2 * SCALE, y: startY }
        sp2 = { x: startX - 2 * SCALE - markWidth, y: startY }
        ep1 = { x: endX - 2 * SCALE, y: endY }
        ep2 = { x: endX - 2 * SCALE - markWidth, y: endY }
        if (info) {
          if (Math.abs(startY - endY) - tw <= 0) {
            this.dxf.addLine(
              point3d(sp1.x - markWidth / 2, this.fy(sp1.y)),
              point3d(ep1.x - markWidth / 2, this.fy(ep1.y)),
              { layerName: LAYER_DIMENSIONS }
            )
            const ty = sp2.y
            addTextWithStyle(
              this.dxf,
              point3d(sp2.x, this.fy(ty)),
              textH,
              info,
              { layerName: LAYER_DIMENSIONS, rotation: 90 }
            )
          } else {
            this.dxf.addLine(
              point3d(sp1.x - markWidth / 2, this.fy(sp1.y)),
              point3d(ep1.x - markWidth / 2, this.fy(ep1.y)),
              { layerName: LAYER_DIMENSIONS }
            )
            const ty = (startY + endY) / 2 - tw / 2
            addTextWithStyle(
              this.dxf,
              point3d(sp2.x, this.fy(ty)),
              textH,
              info,
              { layerName: LAYER_DIMENSIONS, rotation: 90 }
            )
          }
        }
        break
      case 'right':
        sp1 = { x: startX + 2 * SCALE, y: startY }
        sp2 = { x: startX + 2 * SCALE + markWidth, y: startY }
        ep1 = { x: endX + 2 * SCALE, y: endY }
        ep2 = { x: endX + 2 * SCALE + markWidth, y: endY }
        if (info) {
          if (Math.abs(startY - endY) - tw <= 0) {
            this.dxf.addLine(
              point3d(sp1.x + markWidth / 2, this.fy(sp1.y)),
              point3d(sp1.x + markWidth / 2, this.fy(sp1.y - tw)),
              { layerName: LAYER_DIMENSIONS }
            )
            this.dxf.addLine(
              point3d(ep1.x + markWidth / 2, this.fy(ep1.y)),
              point3d(ep1.x + markWidth / 2, this.fy(ep1.y + 2 * SCALE)),
              { layerName: LAYER_DIMENSIONS }
            )
            addTextWithStyle(
              this.dxf,
              point3d(
                sp1.x + markWidth / 2,
                this.fy((Math.abs(startY - endY) - tw) / 2 + startY)
              ),
              textH,
              info,
              { layerName: LAYER_DIMENSIONS, rotation: 90 }
            )
          } else {
            this.dxf.addLine(
              point3d(sp1.x + markWidth / 2, this.fy(sp1.y)),
              point3d(ep1.x + markWidth / 2, this.fy(ep1.y)),
              { layerName: LAYER_DIMENSIONS }
            )
            addTextWithStyle(
              this.dxf,
              point3d(
                sp2.x + markWidth / 2,
                this.fy((startY + endY) / 2 - tw / 2)
              ),
              textH,
              info,
              { layerName: LAYER_DIMENSIONS, rotation: 90 }
            )
          }
        }
        break
      case 'top':
        sp1 = { x: startX, y: startY - 1 * SCALE }
        sp2 = { x: startX, y: startY - 1 * SCALE - markWidth }
        ep1 = { x: endX, y: endY - 1 * SCALE }
        ep2 = { x: endX, y: endY - 1 * SCALE - markWidth }
        if (info) {
          if (Math.abs(sp1.x - ep1.x) - tw <= 0) {
            this.dxf.addLine(
              point3d(sp1.x, this.fy(sp1.y - markWidth / 2)),
              point3d(ep1.x, this.fy(ep1.y - markWidth / 2)),
              { layerName: LAYER_DIMENSIONS }
            )
            addTextWithStyle(
              this.dxf,
              point3d(
                sp1.x,
                this.fy((Math.abs(startY - endY) - tw) / 2 + startY - textH)
              ),
              textH,
              info,
              { layerName: LAYER_DIMENSIONS }
            )
          } else {
            this.dxf.addLine(
              point3d(sp1.x, this.fy(sp1.y - markWidth / 2)),
              point3d(ep1.x, this.fy(ep1.y - markWidth / 2)),
              { layerName: LAYER_DIMENSIONS }
            )
            addTextWithStyle(
              this.dxf,
              point3d(
                (Math.abs(sp1.x - ep1.x) - tw) / 2 + startX,
                this.fy(sp1.y - markWidth / 2)
              ),
              textH,
              info,
              { layerName: LAYER_DIMENSIONS }
            )
          }
        }
        break
      case 'bottom':
        sp1 = { x: startX, y: startY + 1 * SCALE }
        sp2 = { x: startX, y: startY + 1 * SCALE + markWidth }
        ep1 = { x: endX, y: endY + 1 * SCALE }
        ep2 = { x: endX, y: endY + 1 * SCALE + markWidth }
        if (info) {
          if (Math.abs(sp1.x - ep1.x) - tw <= 0) {
            this.dxf.addLine(
              point3d(sp1.x, this.fy(sp1.y + markWidth / 2)),
              point3d(ep1.x, this.fy(ep1.y + markWidth / 2)),
              { layerName: LAYER_DIMENSIONS }
            )
            addTextWithStyle(
              this.dxf,
              point3d(sp1.x + markWidth / 2, this.fy(sp1.y)),
              textH,
              info,
              { layerName: LAYER_DIMENSIONS }
            )
          } else {
            this.dxf.addLine(
              point3d(sp1.x, this.fy(sp1.y + markWidth / 2)),
              point3d(ep1.x, this.fy(ep1.y + markWidth / 2)),
              { layerName: LAYER_DIMENSIONS }
            )
            addTextWithStyle(
              this.dxf,
              point3d(
                (Math.abs(sp1.x - ep1.x) - tw) / 2 + startX,
                this.fy(sp1.y)
              ),
              textH,
              info,
              { layerName: LAYER_DIMENSIONS }
            )
          }
        }
        break
    }
    this.dxf.addLine(
      point3d(sp1.x, this.fy(sp1.y)),
      point3d(sp2.x, this.fy(sp2.y)),
      { layerName: LAYER_DIMENSIONS }
    )
    this.dxf.addLine(
      point3d(ep1.x, this.fy(ep1.y)),
      point3d(ep2.x, this.fy(ep2.y)),
      { layerName: LAYER_DIMENSIONS }
    )
  }

  private drawFrontView(part: PartType) {
    this.leftPointX = this.offsetX
    this.bottomPointY = this.offsetY + this.partHeight
    this.drawSlots(this.offsetX, this.offsetY, part.slots)
    this.drawHoles(this.offsetX, this.offsetY, part.holes)
    if (part.oPath && part.curveHoles) {
      part.curveHoles.forEach((hole) => {
        this.drawPath(hole.path)
      })
    }
    if (part.oPath && part.oPath.length > 1) {
      for (let i = 1; i < part.oPath.length; i++) {
        const internalPath = part.oPath[i].map((point) => ({
          x: point.x,
          y: point.y,
        }))
        internalPath.push({
          x: part.oPath[i][0].x,
          y: part.oPath[i][0].y,
        })
        this.drawPath(internalPath)
      }
    }
    if (part.sholes) {
      part.sholes.forEach((shole) => {
        const startX = shole.ocenter.x + this.offsetX
        const startY = shole.ocenter.y + this.offsetY
        const deep = shole.deep
        if ([1, 3].includes(Number(shole.side))) {
          if (startX >= this.partWidth + this.offsetX) {
            this.drawDashLine(startX - deep, startY, deep, 'h')
          } else {
            this.drawDashLine(startX, startY, deep, 'h')
          }
        } else {
          if (startY >= this.partHeight + this.offsetY) {
            this.drawDashLine(startX, startY - deep, deep, 'v')
          } else {
            this.drawDashLine(startX, startY, deep, 'v')
          }
        }
      })
    }

    if (part.oPath && part.oPath[0].length > 4) {
      const path = part.oPath[0].map((point) => ({ x: point.x, y: point.y }))
      path.push({ x: part.oPath[0][0].x, y: part.oPath[0][0].y })
      this.drawPath(path)
    } else {
      this.dxf.addRectangle(
        point2d(this.offsetX, this.fy(this.offsetY)),
        point2d(
          this.offsetX + this.partWidth,
          this.fy(this.offsetY + this.partHeight)
        ),
        { layerName: LAYER_OUTLINE }
      )
    }

    this.dealMarkHoleSlot(
      { holes: part.holes, slots: part.slots },
      { x: this.offsetX, y: this.offsetY + this.partHeight },
      'bottom'
    )
    this.dealMarkHoleSlot(
      { holes: part.holes, slots: part.slots },
      { x: this.offsetX, y: this.offsetY },
      'left'
    )

    this.drawSizeInfo(
      { x: this.offsetX, y: this.offsetY },
      { x: this.offsetX + this.partWidth, y: this.offsetY },
      'top',
      Number(this.part?.oRect.width).toFixed(1)
    )
    this.drawSizeInfo(
      { x: this.offsetX + this.partWidth, y: this.offsetY },
      { x: this.offsetX + this.partWidth, y: this.offsetY + this.partHeight },
      'right',
      Number(this.part?.oRect.height).toFixed(1)
    )
  }

  private drawSideView(part: PartType) {
    const sideMap = new Map([
      [1, 'left'],
      [2, 'bottom'],
      [3, 'right'],
      [4, 'top'],
    ])
    const sideHoleObj: SideHoleObjType = part.sholes.reduce(
      (res: SideHoleObjType, cur) => {
        const dir = sideMap.get(Number(cur.side)) as keyof typeof sideHoleObj
        res[dir].push(cur)
        return res
      },
      { left: [], top: [], right: [], bottom: [] }
    )
    const thick = Number(part.thick)

    if (sideHoleObj.top && sideHoleObj.top.length) {
      const topOff = 20 * SCALE
      this.dxf.addRectangle(
        point2d(this.offsetX, this.fy(this.offsetY - topOff)),
        point2d(
          this.offsetX + this.partWidth,
          this.fy(this.offsetY - topOff + thick)
        ),
        { layerName: LAYER_SIDE_HOLES }
      )
      const tHoles = sideHoleObj.top.map((hole) => ({
        ...hole,
        center: { x: hole.center.y, y: hole.center.x },
      }))
      const holeObj = tHoles.reduce((res: { [key: string]: HoleType }, cur) => {
        const key = String(cur.center.x)
        if (!res[key]) res[key] = cur
        return res
      }, {})
      this.drawSizeInfo(
        { x: this.offsetX, y: this.offsetY - topOff },
        { x: this.offsetX, y: this.offsetY - topOff + thick },
        'left',
        `${Number(part.thick).toFixed(1)}`
      )
      let idx = 0
      for (const key in holeObj) {
        const baseX = this.offsetX + this.partWidth + 6 * SCALE * (idx + 1)
        const baseY = this.offsetY - topOff
        const hole = holeObj[key]
        this.drawSizeInfo(
          { x: baseX, y: baseY },
          { x: baseX, y: baseY + hole.center.x },
          'left',
          String(hole.center.x)
        )
        idx++
      }
      this.drawHoles(
        this.offsetX,
        this.offsetY - topOff,
        sideHoleObj.top,
        'top'
      )
      this.dealMarkHoleSlot(
        {
          holes: tHoles.map((hole) => ({
            ...hole,
            center: { x: hole.center.y, y: hole.center.x },
          })),
        },
        { x: this.offsetX, y: this.offsetY - 30 * SCALE },
        'bottom'
      )
    }

    if (sideHoleObj.bottom && sideHoleObj.bottom.length) {
      const botOff = 10 * SCALE
      this.dxf.addRectangle(
        point2d(this.offsetX, this.fy(this.bottomPointY + botOff)),
        point2d(
          this.offsetX + this.partWidth,
          this.fy(this.bottomPointY + botOff + thick)
        ),
        { layerName: LAYER_SIDE_HOLES }
      )
      const bHoles = sideHoleObj.bottom.map((hole) => ({
        ...hole,
        center: { x: part.oRect.width - hole.center.x, y: hole.center.y },
      }))
      const holeObj = bHoles.reduce((res: { [key: string]: HoleType }, cur) => {
        const key = String(cur.center.y)
        if (!res[key]) res[key] = cur
        return res
      }, {})
      let idx = 0
      for (const key in holeObj) {
        const baseX = this.offsetX + this.partWidth + 6 * SCALE * (idx + 1)
        const baseY = this.bottomPointY + botOff
        const hole = holeObj[key]
        this.drawSizeInfo(
          { x: baseX, y: baseY },
          { x: baseX, y: baseY + hole.center.y },
          'left',
          String(hole.center.y)
        )
        idx++
      }
      const bottomHoles = sideHoleObj.bottom.map((hole) => ({
        ...hole,
        center: { x: part.oRect.width - hole.center.x, y: hole.center.y },
      }))
      this.drawHoles(
        this.offsetX,
        this.bottomPointY + botOff,
        bottomHoles,
        'bottom'
      )
      this.dealMarkHoleSlot(
        { holes: bHoles },
        { x: this.offsetX, y: this.bottomPointY + 20 * SCALE },
        'bottom'
      )
    }

    if (sideHoleObj.left && sideHoleObj.left.length) {
      const leftOff = 20 * SCALE
      this.dxf.addRectangle(
        point2d(this.leftPointX - leftOff, this.fy(this.offsetY)),
        point2d(
          this.leftPointX - leftOff + thick,
          this.fy(this.offsetY + this.partHeight)
        ),
        { layerName: LAYER_SIDE_HOLES }
      )
      this.drawSizeInfo(
        { x: this.leftPointX - leftOff, y: this.offsetY },
        { x: this.leftPointX - leftOff + thick, y: this.offsetY },
        'top',
        `${Number(part.thick).toFixed(1)}`
      )
      const leftHoles = sideHoleObj.left.map((hole) => ({
        ...hole,
        center: { x: part.oRect.height - hole.center.x, y: hole.center.y },
      }))
      this.drawHoles(this.leftPointX - leftOff, this.offsetY, leftHoles, 'left')
      this.dealMarkHoleSlot(
        {
          holes: leftHoles.map((hole) => ({
            ...hole,
            center: { x: hole.center.y, y: hole.center.x },
          })),
        },
        { x: this.leftPointX - leftOff, y: this.offsetY },
        'left',
        'left'
      )
      this.dealMarkHoleSlot(
        {
          holes: leftHoles.map((hole) => ({
            ...hole,
            center: { x: hole.center.y, y: hole.center.x },
          })),
        },
        { x: this.leftPointX - leftOff, y: this.offsetY + this.partHeight },
        'bottom',
        'left'
      )
    }

    if (sideHoleObj.right && sideHoleObj.right.length) {
      const rightOff = 20 * SCALE
      this.dxf.addRectangle(
        point2d(
          this.offsetX + this.partWidth + rightOff,
          this.fy(this.offsetY)
        ),
        point2d(
          this.offsetX + this.partWidth + rightOff + thick,
          this.fy(this.offsetY + this.partHeight)
        ),
        { layerName: LAYER_SIDE_HOLES }
      )
      this.drawHoles(
        this.offsetX + this.partWidth + rightOff,
        this.offsetY,
        sideHoleObj.right,
        'right'
      )
      const sHoles = sideHoleObj.right.map((hole) => ({
        ...hole,
        center: { x: hole.center.y, y: hole.center.x },
      }))
      this.dealMarkHoleSlot(
        { holes: sHoles },
        { x: this.offsetX + this.partWidth + rightOff, y: this.offsetY },
        'left',
        'right'
      )
      this.dealMarkHoleSlot(
        { holes: sHoles },
        {
          x: this.offsetX + this.partWidth + rightOff,
          y: this.offsetY + this.partHeight,
        },
        'bottom',
        'right'
      )
      this.drawSizeInfo(
        { x: this.offsetX + this.partWidth + rightOff, y: this.offsetY },
        {
          x: this.offsetX + this.partWidth + rightOff + thick,
          y: this.offsetY,
        },
        'top',
        `${Number(part.thick).toFixed(1)}`
      )
    }
  }

  drawThreeView(part: PartType, drawBackCall?: () => void) {
    this.init(part)
    this.drawFrontView(part)
    this.drawSideView(part)
    drawBackCall && drawBackCall()
  }

  private drawHoles(
    startX: number,
    startY: number,
    holes: HoleType[],
    side?: 'left' | 'right' | 'top' | 'bottom'
  ) {
    const holeObj = holes.reduce((res: { [key: string]: HoleType[] }, cur) => {
      const key = String(cur.deep) + String(cur.diameter) + String(cur.side)
      if (res[key]) res[key].push(cur)
      else res[key] = [cur]
      return res
    }, {})

    for (const key in holeObj) {
      const minX = Math.min(
        ...holeObj[key].map((hole) => {
          if (side)
            return ['left', 'right'].includes(side)
              ? hole.center.y
              : hole.center.x
          return hole.ocenter.x
        })
      )
      const maxY = Math.max(
        ...holeObj[key]
          .filter((hole) => {
            if (side)
              return ['left', 'right'].includes(side)
                ? hole.center.y <= minX
                : hole.center.x <= minX
            return hole.ocenter.x <= minX
          })
          .map((h) => {
            if (side)
              return ['left', 'right'].includes(side) ? h.center.x : h.center.y
            return h.ocenter.y
          })
      )

      holeObj[key].forEach((hole) => {
        let { x, y } = hole.ocenter
        if (side && ['left', 'right'].includes(side)) {
          x = hole.center.y
          y = hole.center.x
        } else if (side && ['top', 'bottom'].includes(side)) {
          x = hole.center.x
          y = hole.center.y
        }
        const cx = x + startX
        const cy = y + startY
        const radius = hole.diameter / 2
        this.dxf.addCircle(point3d(cx, this.fy(cy)), radius, {
          layerName: LAYER_HOLES,
        })

        const textWidth = String(this.holeOrder).length * 2 * SCALE
        addTextWithStyle(
          this.dxf,
          point3d(cx - textWidth / 2, this.fy(cy + 1 * SCALE)),
          2 * SCALE,
          String(this.holeOrder),
          { layerName: LAYER_TEXT }
        )
        if (x <= minX && y >= maxY) {
          const text = `数量${holeObj[key].length} 孔径${Number(
            hole.diameter
          ).toFixed(1)} 孔深${Number(hole.deep).toFixed(1)} ${
            !side ? (hole.side > 0 ? '(正)' : '(反)') : ''
          }`
          const width = hole.diameter
          const offsetPointY = y + startY - width / 2
          const isOverText = offsetPointY - 4 * SCALE < this.offsetY
          this.drawHoleSlotInfo(
            text,
            {
              x: x + startX,
              y: isOverText && !side ? y + startY + width / 2 : offsetPointY,
              height: width,
            },
            side == 'left',
            isOverText && !side ? 'bottom' : 'top',
            !side
          )
        }
      })
      this.holeOrder++
    }
  }

  private dealMarkHoleSlot(
    data: { holes?: HoleType[]; slots?: SlotType[] },
    startPoint: { x: number; y: number },
    dir: 'left' | 'bottom' | 'right' | 'top',
    sideView?: 'left' | 'bottom' | 'right' | 'top'
  ) {
    const isLR = ['left', 'right'].includes(dir)
    const isLRSideView = sideView && ['left', 'right'].includes(sideView)
    const holeCenter = (hole: HoleType) =>
      isLRSideView ? hole.center : hole.ocenter

    const xyBaseHoleObj = data.holes?.reduce(
      (res: { [key: string]: HoleType[] }, cur) => {
        const yBase = isLR ? holeCenter(cur).x : holeCenter(cur).y
        if (res[yBase]) res[yBase].push(cur)
        else res[yBase] = [cur]
        return res
      },
      {}
    )

    let slotsData: SlotInfoType[] | undefined = data.slots?.map((slot) =>
      getSlotBasicInfo(slot, 'opt')
    )

    const newObj: { [key: string]: string } = {}
    for (const key in xyBaseHoleObj) {
      newObj[key] = xyBaseHoleObj[key]
        .map((hole) =>
          Number((isLR ? holeCenter(hole).y : holeCenter(hole).x).toFixed(2))
        )
        .sort((a, b) => a - b)
        .join(',')
    }
    const tempArr: string[] = []
    for (const key in newObj) {
      if (
        tempArr.includes(newObj[key]) ||
        tempArr.some((str) => str.includes(newObj[key]))
      ) {
        if (xyBaseHoleObj) delete xyBaseHoleObj[key]
      } else {
        tempArr.push(newObj[key])
      }
    }
    const offsetWidth = 6 * SCALE
    let index = 0
    const xyBaseHoleArr: HoleType[][] = []
    for (const key in xyBaseHoleObj) {
      xyBaseHoleArr.push(xyBaseHoleObj[key])
    }
    xyBaseHoleArr.sort((a, b) => a.length - b.length)
    xyBaseHoleArr.forEach((holes) => {
      const baseY = startPoint.y + index * offsetWidth + 2 * SCALE
      const baseX = startPoint.x - index * offsetWidth - 2 * SCALE
      let lastPoint = isLR
        ? { x: baseX, y: startPoint.y }
        : { x: startPoint.x, y: baseY }
      let lastHole: HoleType | undefined
      holes
        .sort((h1, h2) =>
          isLR
            ? holeCenter(h1).y - holeCenter(h2).y
            : holeCenter(h1).x - holeCenter(h2).x
        )
        .forEach((hole) => {
          const info = (
            lastHole
              ? Math.abs(
                  isLR
                    ? holeCenter(hole).y - holeCenter(lastHole).y
                    : holeCenter(hole).x - holeCenter(lastHole).x
                )
              : isLR
              ? holeCenter(hole).y
              : holeCenter(hole).x
          ).toFixed(1)
          const curPoint = {
            x: isLR ? baseX : startPoint.x + holeCenter(hole).x,
            y: isLR ? startPoint.y + holeCenter(hole).y : baseY,
          }
          const numInfo = Number(info)
          const displayInfo = numInfo
            ? numInfo == parseInt(info)
              ? String(parseInt(info))
              : numInfo.toFixed(1)
            : ''
          this.drawSizeInfo(lastPoint, curPoint, dir, displayInfo)
          lastHole = hole
          lastPoint = curPoint
          if (!sideView) {
            this.leftPointX =
              lastPoint.x < this.leftPointX ? lastPoint.x : this.leftPointX
            this.bottomPointY =
              lastPoint.y > this.bottomPointY ? lastPoint.y : this.bottomPointY
          }
        })
      lastHole = undefined
      index++
    })

    if (slotsData) {
      const slotObj = slotsData.reduce(
        (res: { [key: string]: SlotInfoType }, cur) => {
          const key = isLR ? `${cur.y}L${cur.height}` : `${cur.x}L${cur.width}`
          if (!res[key]) res[key] = cur
          return res
        },
        {}
      )
      const slotKeys = Object.keys(slotObj)
      const newSlotsArr: SlotInfoType[] = []
      slotKeys.forEach((key) => newSlotsArr.push(slotObj[key]))
      slotsData = newSlotsArr
      slotsData
        .sort((s1, s2) => (isLR ? s1.y - s2.y : s1.x - s2.x))
        .forEach((slot, idx) => {
          const baseY = startPoint.y + index * offsetWidth + 2 * SCALE
          const baseX = startPoint.x - index * offsetWidth - 2 * SCALE
          let lastSlot: typeof slot | undefined
          let lastPoint = isLR
            ? { x: baseX, y: startPoint.y }
            : { x: startPoint.x, y: baseY }
          let info = (
            lastSlot
              ? Math.abs(
                  isLR
                    ? slot.y - (lastSlot.y + lastSlot.height)
                    : slot.x - (lastSlot.x + lastSlot.width)
                )
              : isLR
              ? slot.y
              : slot.x
          ).toFixed(2)
          idx > 0 && lastSlot ? (info = '') : info
          const curPoint = {
            x: isLR ? baseX : startPoint.x + slot.x,
            y: isLR ? startPoint.y + slot.y : baseY,
          }
          const numInfo = Number(info)
          const displayInfo =
            numInfo == parseInt(info)
              ? String(parseInt(info))
              : numInfo.toFixed(1)
          this.drawSizeInfo(lastPoint, curPoint, dir, displayInfo)
          lastPoint = curPoint
          const info2 = Math.abs(isLR ? slot.height : slot.width).toFixed(1)
          const curPoint2 = {
            x: isLR ? baseX : startPoint.x + slot.x + slot.width,
            y: isLR ? startPoint.y + slot.y + slot.height : baseY,
          }
          this.drawSizeInfo(lastPoint, curPoint2, dir, info2)
          lastSlot = slot
          lastPoint = curPoint2
          if (!sideView) {
            this.leftPointX =
              lastPoint.x < this.leftPointX ? lastPoint.x : this.leftPointX
            this.bottomPointY =
              lastPoint.y > this.bottomPointY ? lastPoint.y : this.bottomPointY
          }
          lastSlot = undefined
          index++
        })
    }
  }

  private drawSlots(
    startX: number,
    startY: number,
    slots: SlotType[],
    side?: 'left' | 'right' | 'top' | 'bottom'
  ) {
    const newSlotsArr = slots.map((slot) => getSlotBasicInfo(slot, 'opt'))
    const slotObj = newSlotsArr.reduce(
      (res: { [key: string]: typeof newSlotsArr }, cur) => {
        const key =
          Number(cur.slotData.deep).toFixed(1) +
          Number(cur.height).toFixed(1) +
          Number(cur.width).toFixed(1)
        if (res[key]) res[key].push(cur)
        else res[key] = [cur]
        return res
      },
      {}
    )

    const dealSlotData = (
      slot: SlotInfoType,
      index: number,
      isOverLength: boolean
    ) => {
      this.dxf.addRectangle(
        point2d(slot.x + startX, this.fy(slot.y + startY)),
        point2d(
          slot.x + slot.width + startX,
          this.fy(slot.y + slot.height + startY)
        ),
        { layerName: LAYER_SLOTS }
      )
      const centerPoint = {
        x: slot.x + slot.width / 2 + startX,
        y: slot.y + slot.height / 2 + startY,
      }
      const w = slot.width > slot.height ? slot.height : slot.width
      const deep = slot.slotData.deep
      const text = `槽宽${Number(w).toFixed(1)} 槽深${Number(deep).toFixed(
        1
      )} (${slot.slotData.side > 0 ? '正' : '反'})`
      const height = slot.height
      const isOverText = centerPoint.y - height / 2 - 4 * SCALE < this.offsetY

      if (!isOverLength || (isOverLength && index == 0)) {
        this.drawHoleSlotInfo(
          text,
          {
            x: centerPoint.x,
            y:
              isOverText && !side
                ? centerPoint.y + height / 2
                : centerPoint.y - height / 2,
            height,
          },
          side == 'left',
          isOverText && !side ? 'bottom' : 'top',
          !side
        )
      }
    }

    for (const key in slotObj) {
      slotObj[key].forEach((slot, index) => {
        dealSlotData(slot, index, slotObj[key].length > 9)
      })
    }
  }

  private drawPath(path: { x: number; y: number }[], hasOffset = true) {
    const vertices = path.map((point) => ({
      point: point2d(
        point.x + (hasOffset ? this.offsetX : 0),
        this.fy(point.y + (hasOffset ? this.offsetY : 0))
      ),
    }))
    this.dxf.addLWPolyline(vertices, {
      flags: LWPolylineFlags.Closed,
      layerName: LAYER_OUTLINE,
    })
  }

  private drawHoleSlotInfo(
    text: string,
    startPoint: { x: number; y: number; height: number },
    isLeftView = false,
    dir: 'top' | 'bottom' = 'top',
    isMainView = false
  ) {
    const textHeight = 2.5 * SCALE
    const gap = 4 * SCALE
    const textWidth = estimateTextWidth(text, textHeight)
    const areRectanglesIntersecting = (rect1: RectType, rect2: RectType) => {
      return (
        rect1.x1 < rect2.x2 &&
        rect1.x2 > rect2.x1 &&
        rect1.y1 < rect2.y2 &&
        rect1.y2 > rect2.y1
      )
    }

    if (
      isLeftView ||
      (isMainView &&
        startPoint.x + gap + textWidth > this.offsetX + this.partWidth)
    ) {
      let offsetY = startPoint.y
      const offsetX = startPoint.x - textWidth
      let isInTextRect = true
      let hasChangeDirStatus = false
      if (this.textRectArr.length) {
        while (isInTextRect) {
          offsetY = dir == 'top' ? offsetY - gap : offsetY + gap
          isInTextRect = this.textRectArr.some((rect) => {
            return areRectanglesIntersecting(
              {
                x1: offsetX,
                y1: offsetY - textHeight,
                x2: offsetX + textWidth,
                y2: offsetY,
              },
              rect
            )
          })
          if (
            isMainView &&
            (offsetY < this.offsetY ||
              offsetY > this.offsetY + this.partHeight) &&
            !hasChangeDirStatus
          ) {
            if (dir == 'bottom') {
              dir = 'top'
              offsetY = startPoint.y - startPoint.height
            } else {
              dir = 'bottom'
              offsetY = startPoint.y + startPoint.height
            }
            hasChangeDirStatus = true
            isInTextRect = true
          } else if (hasChangeDirStatus) {
            offsetY = dir == 'top' ? startPoint.y - gap : startPoint.y + gap
            break
          }
        }
      } else {
        offsetY = dir == 'top' ? offsetY - gap : offsetY + gap
      }
      this.dxf.addLine(
        point3d(startPoint.x, this.fy(startPoint.y)),
        point3d(startPoint.x - gap, this.fy(offsetY)),
        { layerName: LAYER_DIMENSIONS }
      )
      this.dxf.addLine(
        point3d(startPoint.x - gap, this.fy(offsetY)),
        point3d(startPoint.x - gap - textWidth, this.fy(offsetY)),
        { layerName: LAYER_DIMENSIONS }
      )
      addTextWithStyle(
        this.dxf,
        point3d(startPoint.x - gap - textWidth, this.fy(offsetY)),
        textHeight,
        text,
        {
          layerName: LAYER_TEXT,
        }
      )
      this.textRectArr.push({
        x1: offsetX,
        y1: offsetY - textHeight,
        x2: offsetX + textWidth,
        y2: offsetY,
      })
    } else if (!isLeftView) {
      let offsetY = startPoint.y
      const offsetX = startPoint.x + gap
      let isInTextRect = true
      let hasChangeDirStatus = false
      if (this.textRectArr.length) {
        while (isInTextRect) {
          offsetY = dir == 'top' ? offsetY - gap : offsetY + gap
          isInTextRect = this.textRectArr.some((rect) => {
            return areRectanglesIntersecting(
              {
                x1: offsetX,
                y1: offsetY - textHeight,
                x2: offsetX + textWidth,
                y2: offsetY,
              },
              rect
            )
          })
          if (
            isMainView &&
            (offsetY < this.offsetY ||
              offsetY > this.offsetY + this.partHeight) &&
            !hasChangeDirStatus
          ) {
            if (dir == 'bottom') {
              dir = 'top'
              offsetY = startPoint.y - startPoint.height
            } else {
              dir = 'bottom'
              offsetY = startPoint.y + startPoint.height
            }
            hasChangeDirStatus = true
            isInTextRect = true
          } else if (hasChangeDirStatus) {
            offsetY = dir == 'top' ? startPoint.y - gap : startPoint.y + gap
            break
          }
        }
      } else {
        offsetY = dir == 'top' ? offsetY - gap : offsetY + gap
      }
      this.dxf.addLine(
        point3d(startPoint.x, this.fy(startPoint.y)),
        point3d(offsetX, this.fy(offsetY)),
        { layerName: LAYER_DIMENSIONS }
      )
      this.dxf.addLine(
        point3d(offsetX, this.fy(offsetY)),
        point3d(offsetX + textWidth, this.fy(offsetY)),
        { layerName: LAYER_DIMENSIONS }
      )
      addTextWithStyle(
        this.dxf,
        point3d(offsetX, this.fy(offsetY)),
        textHeight,
        text,
        {
          layerName: LAYER_TEXT,
        }
      )
      this.textRectArr.push({
        x1: offsetX,
        y1: offsetY - textHeight,
        x2: offsetX + textWidth,
        y2: offsetY,
      })
    }
  }

  private drawDashLine(
    startX: number,
    startY: number,
    long: number,
    dir: 'h' | 'v'
  ) {
    if (dir === 'h') {
      this.dxf.addLine(
        point3d(startX, this.fy(startY)),
        point3d(startX + long, this.fy(startY)),
        {
          layerName: LAYER_DASHED,
          lineType: 'DASHED',
        }
      )
    } else {
      this.dxf.addLine(
        point3d(startX, this.fy(startY)),
        point3d(startX, this.fy(startY + long)),
        {
          layerName: LAYER_DASHED,
          lineType: 'DASHED',
        }
      )
    }
  }

  drawInfoTable(info: TableInfoType) {
    const S = SCALE
    const tableW = 146 * S
    const tableH = 45 * S
    const startX = this.pageWidth - tableW - 5 * SCALE
    const startY = this.pageHeight - tableH - 5 * SCALE
    const textH = 2.5 * S
    const colW = 37 * S
    const rowH = 9 * S
    const divX = startX + 111 * S
    const tableTop = startY + tableH
    this.dxf.addRectangle(
      point2d(startX, this.fy(tableTop)),
      point2d(startX + tableW, this.fy(startY)),
      { layerName: LAYER_TABLE }
    )
    this.dxf.addLine(
      point3d(divX, this.fy(tableTop)),
      point3d(divX, this.fy(startY)),
      { layerName: LAYER_TABLE }
    )

    for (let index = 0; index < 4; index++) {
      if (!index) {
        const texts = [
          `生效日期: ${info.date}`,
          `版本号: ${info.version}`,
          `第 ${info.modifiedCount} 次修改`,
        ]
        for (let i = 1; i < 4; i++) {
          addTextWithStyle(
            this.dxf,
            point3d(startX + colW * (i - 1) + 5 * S, this.fy(startY + 6 * S)),
            textH,
            texts[i - 1],
            { layerName: LAYER_TABLE }
          )
          this.dxf.addLine(
            point3d(startX + colW * i, this.fy(startY)),
            point3d(startX + colW * i, this.fy(startY + rowH)),
            { layerName: LAYER_TABLE }
          )
        }
      }
      this.dxf.addLine(
        point3d(startX, this.fy(startY + rowH * (index + 1))),
        point3d(divX, this.fy(startY + rowH * (index + 1))),
        { layerName: LAYER_TABLE }
      )
    }
    addTextWithStyle(
      this.dxf,
      point3d(startX + 5 * S, this.fy(startY + rowH + 4 * S)),
      textH,
      `设计: ${info.designer}`,
      { layerName: LAYER_TABLE }
    )
    addTextWithStyle(
      this.dxf,
      point3d(startX + 50 * S, this.fy(startY + rowH + 4 * S)),
      textH,
      `规格: ${info.size}`,
      { layerName: LAYER_TABLE }
    )
    addTextWithStyle(
      this.dxf,
      point3d(startX + 5 * S, this.fy(startY + 2 * rowH + 4 * S)),
      textH,
      `制图: ${info.pic}`,
      { layerName: LAYER_TABLE }
    )
    addTextWithStyle(
      this.dxf,
      point3d(startX + 50 * S, this.fy(startY + 2 * rowH + 4 * S)),
      textH,
      `材料: ${info.matCode}`,
      { layerName: LAYER_TABLE }
    )
    addTextWithStyle(
      this.dxf,
      point3d(startX + 5 * S, this.fy(startY + 3 * rowH + 4 * S)),
      textH,
      `校对: ${info.check}`,
      { layerName: LAYER_TABLE }
    )
    addTextWithStyle(
      this.dxf,
      point3d(startX + 50 * S, this.fy(startY + 3 * rowH + 4 * S)),
      textH,
      `颜色: ${info.color}`,
      { layerName: LAYER_TABLE }
    )
    addTextWithStyle(
      this.dxf,
      point3d(startX + 85 * S, this.fy(startY + 3 * rowH + 4 * S)),
      textH,
      `数量: ${info.count}`,
      { layerName: LAYER_TABLE }
    )
    addTextWithStyle(
      this.dxf,
      point3d(startX + 5 * S, this.fy(startY + 4 * rowH + 4 * S)),
      textH,
      `审批: ${info.approval}`,
      { layerName: LAYER_TABLE }
    )
    addTextWithStyle(
      this.dxf,
      point3d(startX + 50 * S, this.fy(startY + 4 * rowH + 4 * S)),
      textH,
      `比例: ${info.percent}`,
      { layerName: LAYER_TABLE }
    )
    addTextWithStyle(
      this.dxf,
      point3d(startX + 85 * S, this.fy(startY + 4 * rowH + 4 * S)),
      textH,
      `共${info.total}张 第${info.index}张`,
      { layerName: LAYER_TABLE }
    )
    this.dxf.addLine(
      point3d(startX + 80 * S, this.fy(startY + 3 * rowH)),
      point3d(startX + 80 * S, this.fy(tableTop)),
      { layerName: LAYER_TABLE }
    )
    this.dxf.addLine(
      point3d(startX + 45 * S, this.fy(startY + rowH)),
      point3d(startX + 45 * S, this.fy(tableTop)),
      { layerName: LAYER_TABLE }
    )

    const rightRowH = 12 * S
    for (let idx = 1; idx <= 3; idx++) {
      this.dxf.addLine(
        point3d(divX, this.fy(startY + rightRowH * idx)),
        point3d(startX + tableW, this.fy(startY + rightRowH * idx)),
        { layerName: LAYER_TABLE }
      )
    }
    addTextWithStyle(
      this.dxf,
      point3d(divX + 2 * S, this.fy(startY + 6 * S)),
      textH,
      info.plankNum,
      {
        layerName: LAYER_ANNOTATIONS,
        colorNumber: Colors.Red,
      }
    )
    addTextWithStyle(
      this.dxf,
      point3d(divX + 2 * S, this.fy(startY + rightRowH + 6 * S)),
      textH,
      info.orderNo ?? '',
      {
        layerName: LAYER_ANNOTATIONS,
        colorNumber: Colors.Red,
      }
    )
    const partNameStr = info.partName || ''
    const maxLen = 10 * S
    const textArr = partNameStr.length <= maxLen ? [partNameStr] : []
    if (partNameStr.length > maxLen) {
      const rows = Math.ceil(partNameStr.length / maxLen)
      for (let i = 0; i < rows; i++) {
        textArr.push(partNameStr.slice(i * maxLen, (i + 1) * maxLen))
      }
    }
    textArr.forEach((str, i) => {
      addTextWithStyle(
        this.dxf,
        point3d(divX + 2 * S, this.fy(startY + 23 * S + 6 * S + i * 3 * textH)),
        textH,
        str,
        {
          layerName: LAYER_ANNOTATIONS,
          colorNumber: Colors.Red,
        }
      )
    })
    addTextWithStyle(
      this.dxf,
      point3d(divX + 2 * S, this.fy(startY + 35 * S + 6 * S)),
      textH,
      String(info.count),
      {
        layerName: LAYER_ANNOTATIONS,
        colorNumber: Colors.Red,
      }
    )
  }

  stringify(): string {
    return this.dxf.stringify()
  }

  save(fileName: string) {
    const content = this.stringify()
    const blob = new Blob([content], { type: 'application/dxf' })
    FileSaver.saveAs(blob, `${fileName}.dxf`)
  }
}

export async function exportHoleSlotDxf(
  parts: PartType[],
  isOther?: 'other',
  zipFileName?: string
) {
  const zip = new JSZip()
  parts.forEach((part) => {
    const instance = new GenerateHoleSlotDxf('landscape')
    instance.generate(part, isOther)
    const fileName = part.plankNum ?? part.partName ?? part.name ?? '孔槽图'
    zip.file(`${fileName}.dxf`, instance.stringify())
  })
  const blob = await zip.generateAsync({ type: 'blob' })
  FileSaver.saveAs(blob, `${zipFileName ?? '孔槽图DXF'}.zip`)
}

export async function exportThreeViewDxf(
  parts: PartType[],
  genSimpleLabelBarcode?: boolean,
  zipFileName?: string
) {
  const zip = new JSZip()
  parts.forEach((part, idx) => {
    const newPart = cloneDeep(part)
    // 强制 180 度旋转，主要是部分用户觉得我们的槽位置不对，产品要求 180 度旋转
    rotate180Part(newPart)
    if (part.oRect.width < part.oRect.height) {
      rotatePart(newPart)
    }
    const plankNum = genSimpleLabelBarcode
      ? part.simplePlankNum ?? ''
      : part.plankNum ?? ''
    const drawView = new DrawPartViewDxf()
    drawView.drawThreeView(newPart, () => {
      const tableInfo: TableInfoType = {
        date: ' ',
        version: ' ',
        modifiedCount: '',
        designer: ' ',
        size: `${Number(part.oRect.width).toFixed(1)}*${Number(
          part.oRect.height
        ).toFixed(1)}*${Number(part.thick).toFixed(1)}`,
        pic: ' ',
        matCode: part.matCode,
        check: ' ',
        color: part.texture,
        count: part.amount ? part.amount.toString() : '1',
        percent: ' ',
        approval: ' ',
        index: idx + 1,
        total: parts.length,
        plankNum,
        orderNo: part.orderNo,
        partName: part.partName,
        proCount: ' ',
      }
      drawView.drawInfoTable(tableInfo)
    })
    const fileName = plankNum || part.partName
    zip.file(`${fileName}.dxf`, drawView.stringify())
  })
  const blob = await zip.generateAsync({ type: 'blob' })
  FileSaver.saveAs(blob, `${zipFileName ?? '三视图DXF'}.zip`)
}
